import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import Flatpickr from "react-flatpickr";
import { Polish } from "flatpickr/dist/l10n/pl";
import "flatpickr/dist/themes/airbnb.css";

import { CategoryDropdown } from "./CategoryDropdown";
import { LocationRadiusFilter } from "./LocationRadiusFilter";

import "../styles/EventFilters.css";

// helper
function toYMD(date) {
  if (!date) return "";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// stable stringify for objects (order-independent keys)
function stableStringify(value) {
  if (value === null || value === undefined) return "";
  if (typeof value !== "object") return String(value);

  const seen = new WeakSet();
  const sorter = (a, b) => (a > b ? 1 : a < b ? -1 : 0);

  const normalize = (v) => {
    if (v === null || v === undefined) return v;
    if (typeof v !== "object") return v;
    if (seen.has(v)) return "[Circular]";
    seen.add(v);

    if (Array.isArray(v)) return v.map(normalize);

    const out = {};
    Object.keys(v)
      .sort(sorter)
      .forEach((k) => {
        out[k] = normalize(v[k]);
      });
    return out;
  };

  try {
    return JSON.stringify(normalize(value));
  } catch {
    return String(value);
  }
}

// normalize radius: number/string => number, object => stable string
function normalizeRadius(val) {
  if (val === null || val === undefined || val === "") return null;
  if (typeof val === "number") return Number.isFinite(val) ? val : null;
  if (typeof val === "string") {
    const n = Number(val);
    return Number.isFinite(n) ? n : null;
  }
  // object / complex
  return stableStringify(val);
}

function normalizeArray(arr) {
  const a = Array.isArray(arr) ? arr.slice() : [];
  return a.map(String).sort();
}

function normalizeBoolObj(obj, keys) {
  const out = {};
  keys.forEach((k) => {
    out[k] = !!(obj && obj[k]);
  });
  return out;
}

function shallowEqualNormalized(a, b) {
  if (!a || !b) return false;

  if (a.query !== b.query) return false;
  if (a.dateFrom !== b.dateFrom) return false;
  if (a.dateTo !== b.dateTo) return false;
  if (a.sort !== b.sort) return false;

  if (a.locationText !== b.locationText) return false;

  if (a.locationRadius !== b.locationRadius) return false;

  if (a.selectedCategories.length !== b.selectedCategories.length) return false;
  for (let i = 0; i < a.selectedCategories.length; i++) {
    if (a.selectedCategories[i] !== b.selectedCategories[i]) return false;
  }

  if (a.priceFilters.free !== b.priceFilters.free) return false;
  if (a.priceFilters.paid !== b.priceFilters.paid) return false;

  if (a.locationFilters.online !== b.locationFilters.online) return false;
  if (a.locationFilters.live !== b.locationFilters.live) return false;

  return true;
}

const SORT_OPTIONS = [
  { value: "soon", label: "Najszybciej" },
  { value: "popular", label: "Najpopularniejsze" },
  { value: "new", label: "Najnowsze" },
];

function pushGtmEvent(eventName, params = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...params });
  }

function SortDropdown({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);

  const current = SORT_OPTIONS.find((o) => o.value === value) || SORT_OPTIONS[0];

  // zamykanie po kliknięciu poza
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (!e.target.closest(".sort-dropdown")) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isOpen]);

  const handleSelect = (opt) => {
    onChange && onChange(opt.value);
    setIsOpen(false);
  };


  return (
    <div className={`modern-sort sort-dropdown ${isOpen ? "sort-dropdown--open" : ""}`}>
      <button
        type="button"
        className="sort-dropdown__trigger"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
      >
        <i className="bi bi-filter-circle me-1" />
        <span className="sort-dropdown__label">{current.label}</span>
        <i className={`bi ms-1 ${isOpen ? "bi-chevron-up" : "bi-chevron-down"}`} />
      </button>

      {isOpen && (
        <ul className="sort-dropdown__menu">
          {SORT_OPTIONS.map((opt) => (
            <li key={opt.value}>
              <button
                type="button"
                className={
                  "sort-dropdown__option" +
                  (opt.value === current.value ? " sort-dropdown__option--active" : "")
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(opt);
                }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function EventFilters({
  query,
  onQueryChange,

  dateFrom,
  dateTo,
  onDateChange,

  sort,
  onSortChange,

  selectedCategories,
  onSelectedCategoriesChange,

  priceFilters,
  onPriceFiltersChange,

  locationFilters,
  onLocationFiltersChange,

  locationRadius,
  onLocationRadiusChange,

  locationText,
  onLocationTextChange,

  isCompact,

  onClearFilters,
  onSubmit,
  isDirty,
}) {
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const panelRef = useRef(null);
  const toggleRef = useRef(null);

  const [draft, setDraft] = useState(() => ({
    query: query ?? "",
    dateFrom: dateFrom ?? "",
    dateTo: dateTo ?? "",
    sort: sort ?? "soon",
    selectedCategories: selectedCategories ?? [],
    priceFilters: priceFilters ?? { free: false, paid: false },
    locationFilters: locationFilters ?? { online: false, live: false },
    locationRadius: locationRadius ?? null,
    locationText: locationText ?? "",
  }));

  // sync draft gdy parent zmieni applied
  useEffect(() => {
    setDraft({
      query: query ?? "",
      dateFrom: dateFrom ?? "",
      dateTo: dateTo ?? "",
      sort: sort ?? "soon",
      selectedCategories: selectedCategories ?? [],
      priceFilters: priceFilters ?? { free: false, paid: false },
      locationFilters: locationFilters ?? { online: false, live: false },
      locationRadius: locationRadius ?? null,
      locationText: locationText ?? "",
    });
  }, [
    query,
    dateFrom,
    dateTo,
    sort,
    selectedCategories,
    priceFilters,
    locationFilters,
    locationRadius,
    locationText,
  ]);

  const buildAppliedFiltersPayload = () => {
    const qVal = (draft.query ?? "").trim();

    const cats = Array.isArray(draft.selectedCategories) ? draft.selectedCategories : [];

    const priceTypes = [];
    if (draft.priceFilters?.free) priceTypes.push("free");
    if (draft.priceFilters?.paid) priceTypes.push("paid");

    const locationTypes = [];
    if (draft.locationFilters?.online) locationTypes.push("online");
    if (draft.locationFilters?.live) locationTypes.push("live");

    const hasLocation = !!(draft.locationRadius?.lat && draft.locationRadius?.lng);

    return {
      q: qVal || undefined,
      categories: cats.length ? cats.join(",") : undefined,
      price_type: priceTypes.length ? priceTypes.join(",") : undefined,
      location_type: locationTypes.length ? locationTypes.join(",") : undefined,
      date_from: draft.dateFrom || undefined,
      date_to: draft.dateTo || undefined,
      sort: draft.sort || undefined,
      has_location: hasLocation,
      radius_km: hasLocation ? (draft.locationRadius.radiusKm || 10) : undefined,
      location_label: hasLocation ? (draft.locationRadius.label || undefined) : undefined,
      // opcjonalnie: czy to mobile/desktop
      ui_variant: isCompact ? "mobile" : "desktop",
    };
  };


  const applyDraftToParent = () => {

    if (hasPendingChanges) {
      pushGtmEvent("events_filters_applied", buildAppliedFiltersPayload());
    }

    onQueryChange?.(draft.query);
    onDateChange?.(draft.dateFrom, draft.dateTo);
    onSortChange?.(draft.sort);

    onSelectedCategoriesChange?.(draft.selectedCategories);
    onPriceFiltersChange?.(draft.priceFilters);
    onLocationFiltersChange?.(draft.locationFilters);
    onLocationRadiusChange?.(draft.locationRadius);

    onLocationTextChange?.(draft.locationText);

    onSubmit?.();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    applyDraftToParent();
    setFiltersExpanded(false);
  };

  const handleToggleExpanded = () => {
    setFiltersExpanded((prev) => !prev);
  };

  // daty -> draft
  const handleDateChange = (selectedDates) => {
    if (selectedDates.length === 0) {
      setDraft((p) => ({ ...p, dateFrom: "", dateTo: "" }));
    } else if (selectedDates.length === 1) {
      setDraft((p) => ({ ...p, dateFrom: toYMD(selectedDates[0]), dateTo: "" }));
    } else {
      const [start, end] = selectedDates;
      setDraft((p) => ({ ...p, dateFrom: toYMD(start), dateTo: toYMD(end) }));
    }
  };

  const handleClearDate = () => {
    setDraft((p) => ({ ...p, dateFrom: "", dateTo: "" }));
  };

  // compare applied vs draft -> pending changes?
  const normalizedApplied = useMemo(() => {
    return {
      query: (query ?? "").trim(),
      dateFrom: dateFrom ?? "",
      dateTo: dateTo ?? "",
      sort: sort ?? "soon",
      selectedCategories: normalizeArray(selectedCategories),
      priceFilters: normalizeBoolObj(priceFilters, ["free", "paid"]),
      locationFilters: normalizeBoolObj(locationFilters, ["online", "live"]),
      locationRadius: normalizeRadius(locationRadius),
      locationText: (locationText ?? "").trim(),
    };
  }, [query, dateFrom, dateTo, sort, selectedCategories, priceFilters, locationFilters, locationRadius, locationText]);

  const normalizedDraft = useMemo(() => {
    return {
      query: (draft.query ?? "").trim(),
      dateFrom: draft.dateFrom ?? "",
      dateTo: draft.dateTo ?? "",
      sort: draft.sort ?? "soon",
      selectedCategories: normalizeArray(draft.selectedCategories),
      priceFilters: normalizeBoolObj(draft.priceFilters, ["free", "paid"]),
      locationFilters: normalizeBoolObj(draft.locationFilters, ["online", "live"]),
      locationRadius: normalizeRadius(draft.locationRadius),
      locationText: (draft.locationText ?? "").trim(),
    };
  }, [draft]);

  const hasPendingChanges = useMemo(() => {
    return !shallowEqualNormalized(normalizedApplied, normalizedDraft);
  }, [normalizedApplied, normalizedDraft]);

  // OUTSIDE CLICK zamykanie tylko na desktop
  useEffect(() => {
    if (isCompact) return;
    if (!filtersExpanded) return;

    const onPointerDown = (e) => {
      const panelEl = panelRef.current;
      const toggleEl = toggleRef.current;

      const clickedInsidePanel = panelEl?.contains(e.target);
      const clickedToggle = toggleEl?.contains(e.target);

      const clickedInPopup =
        e.target.closest(".flatpickr-calendar") ||
        e.target.closest(".flatpickr-wrapper") ||
        e.target.closest(".dropdown-menu") ||
        e.target.closest(".location-filter__suggestions");

      if (!clickedInsidePanel && !clickedToggle && !clickedInPopup) {
        setFiltersExpanded(false);
      }
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") setFiltersExpanded(false);
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [filtersExpanded, isCompact]);

  // BODY SCROLL LOCK na mobile gdy sheet otwarty
  useEffect(() => {
    if (!isCompact) return;

    const isOpen = filtersExpanded;

    if (!isOpen) {
      const y = document.body.dataset.efScrollY;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.overflow = "";
      document.body.style.width = "";
      document.body.dataset.efScrollY = "";
      if (y) window.scrollTo(0, parseInt(y, 10) || 0);
      return;
    }

    const scrollY = window.scrollY || 0;
    document.body.dataset.efScrollY = String(scrollY);
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";

    return () => {
      const y = document.body.dataset.efScrollY;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.overflow = "";
      document.body.style.width = "";
      document.body.dataset.efScrollY = "";
      if (y) window.scrollTo(0, parseInt(y, 10) || 0);
    };
  }, [isCompact, filtersExpanded]);

  // ESC zamykanie na mobile (sheet w portalu -> bezpieczne)
  useEffect(() => {
    if (!isCompact) return;
    if (!filtersExpanded) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setFiltersExpanded(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isCompact, filtersExpanded]);

  // Portal content: backdrop + sheet (tylko mobile)
  const mobileSheet = isCompact
    ? createPortal(
        <>
          {/* Backdrop */}
          <button
            type="button"
            className={`ef-backdrop ef-portal ${filtersExpanded ? "open" : ""}`}
            aria-label="Zamknij filtry"
            onClick={() => setFiltersExpanded(false)}
          />

          {/* Bottom sheet */}
          <div className={`ef-sheet ef-portal ${filtersExpanded ? "open" : ""}`} aria-hidden={!filtersExpanded}>
            <div
              className="ef-sheet__panel"
              role="dialog"
              aria-modal="true"
              aria-label="Filtry wydarzeń"
              ref={panelRef}
            >
              <div className="ef-sheet__header">
                <div className="ef-sheet__handle" />
                <div className="ef-sheet__title">Filtry</div>

                <div className="ef-sheet__headerActions">
                  <button
                    type="button"
                    className="ef-sheet__ghost"
                    onClick={() => {
                      onClearFilters?.();
                      // opcjonalnie: zamknij sheet po wyczyszczeniu
                      // setFiltersExpanded(false);
                    }}
                    aria-label="Wyczyść filtry"
                  >
                    Wyczyść
                  </button>

                  <button
                    type="button"
                    className="ef-sheet__icon"
                    onClick={() => setFiltersExpanded(false)}
                    aria-label="Zamknij"
                  >
                    <i className="bi bi-x-lg" />
                  </button>
                </div>
              </div>

              <div className="ef-sheet__content">
                <div className="ef-section">
                  <div className="ef-label">Lokalizacja / promień</div>
                  <div className="ef-card">
                    <LocationRadiusFilter
                      value={draft.locationRadius}
                      onChange={(val) => setDraft((p) => ({ ...p, locationRadius: val }))}
                      locationText={draft.locationText}
                      onLocationTextChange={(txt) => setDraft((p) => ({ ...p, locationText: txt }))}
                    />
                  </div>
                </div>

                <div className="ef-section">
                  <div className="ef-label">Data wydarzenia</div>
                  <div className="ef-card ef-date">
                    <i className="bi bi-calendar2-range ef-ico" />
                    <Flatpickr
                      options={{
                        disableMobile: false,
                        mode: "range",
                        dateFormat: "Y-m-d",
                        altInput: false,
                        altFormat: "d M Y",
                        allowInput: false,
                        locale: Polish,
                        altInputClass: "ef-flatpickr",
                      }}
                      value={[
                        draft.dateFrom ? new Date(draft.dateFrom) : null,
                        draft.dateTo ? new Date(draft.dateTo) : null,
                      ].filter(Boolean)}
                      onChange={handleDateChange}
                      className="ef-flatpickr"
                      placeholder="Zakres dat: Od – Do"
                    />
                    {(draft.dateFrom || draft.dateTo) && (
                      <button
                        type="button"
                        className="ef-icon-btn"
                        onClick={handleClearDate}
                        aria-label="Wyczyść daty"
                      >
                        <i className="bi bi-x-lg" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="ef-section">
                  <div className="ef-label">Kategorie</div>
                  <div className="ef-card">
                    <CategoryDropdown
                      selectedNames={draft.selectedCategories}
                      onChange={(next) => setDraft((p) => ({ ...p, selectedCategories: next }))}
                    />
                  </div>
                </div>

                <div className="ef-section">
                  <div className="ef-label">Cena</div>
                  <div className="ef-grid2">
                    <label className={`ef-toggle ${draft.priceFilters?.free ? "is-active" : ""}`}>
                      <input
                        type="checkbox"
                        checked={!!draft.priceFilters?.free}
                        onChange={(e) =>
                          setDraft((p) => ({
                            ...p,
                            priceFilters: { ...p.priceFilters, free: e.target.checked },
                          }))
                        }
                      />
                      <span className="ef-toggle__inner">
                        <i className="bi bi-gift" /> Darmowe
                      </span>
                    </label>

                    <label className={`ef-toggle ${draft.priceFilters?.paid ? "is-active" : ""}`}>
                      <input
                        type="checkbox"
                        checked={!!draft.priceFilters?.paid}
                        onChange={(e) =>
                          setDraft((p) => ({
                            ...p,
                            priceFilters: { ...p.priceFilters, paid: e.target.checked },
                          }))
                        }
                      />
                      <span className="ef-toggle__inner">
                        <i className="bi bi-coin" /> Płatne
                      </span>
                    </label>
                  </div>
                </div>

                <div className="ef-section">
                  <div className="ef-label">Tryb wydarzenia</div>
                  <div className="ef-grid2">
                    <label className={`ef-toggle ${draft.locationFilters?.online ? "is-active" : ""}`}>
                      <input
                        type="checkbox"
                        checked={!!draft.locationFilters?.online}
                        onChange={(e) =>
                          setDraft((p) => ({
                            ...p,
                            locationFilters: { ...p.locationFilters, online: e.target.checked },
                          }))
                        }
                      />
                      <span className="ef-toggle__inner">
                        <i className="bi bi-globe" /> Online
                      </span>
                    </label>

                    <label className={`ef-toggle ${draft.locationFilters?.live ? "is-active" : ""}`}>
                      <input
                        type="checkbox"
                        checked={!!draft.locationFilters?.live}
                        onChange={(e) =>
                          setDraft((p) => ({
                            ...p,
                            locationFilters: { ...p.locationFilters, live: e.target.checked },
                          }))
                        }
                      />
                      <span className="ef-toggle__inner">
                        <i className="bi bi-geo-alt" /> Na żywo
                      </span>
                    </label>
                  </div>
                </div>

                <div className="ef-section">
                  <div className="ef-label">Sortowanie</div>
                  <div className="ef-card">
                    <SortDropdown
                      value={draft.sort}
                      onChange={(val) => setDraft((p) => ({ ...p, sort: val }))}
                    />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="ef-sheet__footer">
                <button
                  type="button"
                  className="ef-sheet__secondary"
                  onClick={() => setFiltersExpanded(false)}
                >
                  Zamknij
                </button>

                <button
                  type="button"
                  className={
                    "ef-sheet__primary" +
                    (hasPendingChanges ? " is-pending" : "") +
                    (!hasPendingChanges ? " is-disabled" : "")
                  }
                  disabled={!hasPendingChanges}
                  onClick={() => {
                    // APPLY + zamknij
                    applyDraftToParent();
                    setFiltersExpanded(false);
                  }}
                >
                  Zastosuj
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )
    : null;

  return (
    <form className={`event-filters ${isCompact ? "is-compact" : ""}`} onSubmit={handleSubmit}>
      {/* ===================== DESKTOP (BEZ ZMIAN) ===================== */}
      {!isCompact && (
        <div className="filters-shell">
          <div className="filters-bar">
            {/* Search */}
            <div className="filters-bar-section filters-bar-search">
              <i className="bi bi-search filters-bar-icon" />
              <input
                type="text"
                className="filters-bar-input"
                placeholder="Szukaj wydarzenia"
                value={draft.query}
                onChange={(e) => setDraft((p) => ({ ...p, query: e.target.value }))}
              />
              {!!draft.query && (
                <button
                  type="button"
                  className="filters-bar-clear"
                  onClick={() => setDraft((p) => ({ ...p, query: "" }))}
                >
                  <i className="bi bi-x-lg" />
                </button>
              )}
            </div>

            <span className="filters-bar-divider" />

            {/* Lokalizacja */}
            <div className="filters-bar-section filters-bar-location">
              <i className="bi bi-geo-alt filters-bar-icon" />
              <LocationRadiusFilter
                value={draft.locationRadius}
                onChange={(val) => setDraft((p) => ({ ...p, locationRadius: val }))}
                locationText={draft.locationText}
                onLocationTextChange={(txt) => setDraft((p) => ({ ...p, locationText: txt }))}
              />
            </div>

            <span className="filters-bar-divider" />

            <button
              ref={toggleRef}
              type="button"
              className="filters-bar-section filters-bar-more"
              onClick={handleToggleExpanded}
            >
              <i className="bi bi-sliders filters-bar-icon" />
              <span className="filters-bar-text">
                {filtersExpanded ? "Mniej filtrów" : "Więcej filtrów"}
              </span>
            </button>

            <button
              type="submit"
              className={"filters-bar-submit" + (hasPendingChanges ? " is-dirty" : "")}
            >
              Szukaj
            </button>
          </div>

          <div
            ref={panelRef}
            className={`filters-expanded ${filtersExpanded ? "open" : ""}`}
            style={{
              maxHeight: filtersExpanded ? "600px" : "0px",
              opacity: filtersExpanded ? 1 : 0,
              overflow: filtersExpanded ? "visible" : "hidden",
              transition: "max-height 0.4s ease, opacity 0.3s ease",
            }}
          >
            <div className="filters-expanded-inner">
              {/* Kalendarz */}
              <div className="modern-date-range">
                <div className="filter-title">Data wydarzenia</div>
                <div className="date-range-wrap">
                  <i className="bi bi-calendar2-range"></i>
                  <Flatpickr
                    options={{
                      disableMobile: true,
                      mode: "range",
                      dateFormat: "Y-m-d",
                      altInput: true,
                      altFormat: "d M Y",
                      allowInput: true,
                      locale: Polish,
                      altInputClass: "flatpickr-input-custom",
                    }}
                    value={[
                      draft.dateFrom ? new Date(draft.dateFrom) : null,
                      draft.dateTo ? new Date(draft.dateTo) : null,
                    ].filter(Boolean)}
                    onChange={handleDateChange}
                    className="flatpickr-input-custom"
                    placeholder="Zakres dat: Od – Do"
                  />
                  {(draft.dateFrom || draft.dateTo) && (
                    <button
                      type="button"
                      className="btn-clear-date"
                      title="Wyczyść zakres"
                      onClick={handleClearDate}
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
              </div>

              {/* Kategorie */}
              <div className="category-filter">
                <div className="filter-title">Kategorie</div>
                <CategoryDropdown
                  selectedNames={draft.selectedCategories}
                  onChange={(next) => setDraft((p) => ({ ...p, selectedCategories: next }))}
                />
              </div>

              {/* Cena */}
              <div className="price-type-filter">
                <div className="price-type-filter__title">Cena</div>

                <div className="price-type-filter__grid">
                  <label className={`type-card ${draft.priceFilters?.free ? "is-active" : ""}`}>
                    <input
                      type="checkbox"
                      checked={!!draft.priceFilters?.free}
                      onChange={(e) =>
                        setDraft((p) => ({
                          ...p,
                          priceFilters: { ...p.priceFilters, free: e.target.checked },
                        }))
                      }
                    />
                    <div className="type-card__content">
                      <div className="type-card__icon">
                        <i className="bi bi-gift" />
                      </div>
                      <span className="type-card__label">Darmowe</span>
                      <span className="type-card__check" aria-hidden="true" />
                    </div>
                  </label>

                  <label className={`type-card ${draft.priceFilters?.paid ? "is-active" : ""}`}>
                    <input
                      type="checkbox"
                      checked={!!draft.priceFilters?.paid}
                      onChange={(e) =>
                        setDraft((p) => ({
                          ...p,
                          priceFilters: { ...p.priceFilters, paid: e.target.checked },
                        }))
                      }
                    />
                    <div className="type-card__content">
                      <div className="type-card__icon">
                        <i className="bi bi-coin" />
                      </div>
                      <span className="type-card__label">Płatne</span>
                      <span className="type-card__check" aria-hidden="true" />
                    </div>
                  </label>
                </div>
              </div>

              {/* Lokalizacja */}
              <div className="event-type-filter">
                <div className="event-type-filter__title">Lokalizacja</div>

                <div className="event-type-filter__grid">
                  <label className={`type-card ${draft.locationFilters?.online ? "is-active" : ""}`}>
                    <input
                      type="checkbox"
                      checked={!!draft.locationFilters?.online}
                      onChange={(e) =>
                        setDraft((p) => ({
                          ...p,
                          locationFilters: { ...p.locationFilters, online: e.target.checked },
                        }))
                      }
                    />
                    <div className="type-card__content">
                      <div className="type-card__icon">
                        <i className="bi bi-globe" />
                      </div>
                      <span className="type-card__label">Online</span>
                      <span className="type-card__check" aria-hidden="true" />
                    </div>
                  </label>

                  <label className={`type-card ${draft.locationFilters?.live ? "is-active" : ""}`}>
                    <input
                      type="checkbox"
                      checked={!!draft.locationFilters?.live}
                      onChange={(e) =>
                        setDraft((p) => ({
                          ...p,
                          locationFilters: { ...p.locationFilters, live: e.target.checked },
                        }))
                      }
                    />
                    <div className="type-card__content">
                      <div className="type-card__icon">
                        <i className="bi bi-geo-alt" />
                      </div>
                      <span className="type-card__label">Na żywo</span>
                      <span className="type-card__check" aria-hidden="true" />
                    </div>
                  </label>
                </div>
              </div>

              {/* Sort */}
              <div>
                <div className="filter-title">Sortowanie</div>
                <SortDropdown value={draft.sort} onChange={(val) => setDraft((p) => ({ ...p, sort: val }))} />
              </div>

              {/* Reset */}
              <div className="filters-toolbar d-flex flex-wrap gap-2 align-items-center">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm rounded-pill ms-auto"
                  onClick={onClearFilters}
                >
                  <i className="bi bi-x-circle me-1" />
                  Wyczyść filtry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===================== MOBILE (TOP BAR) ===================== */}
      {isCompact && (
        <>
          <div className="efm-wrap">
            <div className="efm-bar">
              <div className="efm-search">
                <i className="bi bi-search efm-ico" />
                <input
                  className="efm-input"
                  placeholder="Szukaj wydarzenia"
                  value={draft.query}
                  onChange={(e) => setDraft((p) => ({ ...p, query: e.target.value }))}
                />
                {!!draft.query && (
                  <button
                    type="button"
                    className="efm-clear"
                    aria-label="Wyczyść"
                    onClick={() => setDraft((p) => ({ ...p, query: "" }))}
                  >
                    <i className="bi bi-x-lg" />
                  </button>
                )}
              </div>

              <button
                ref={toggleRef}
                type="button"
                className={`efm-btn efm-btn--ghost ${filtersExpanded ? "is-active" : ""}`}
                onClick={() => setFiltersExpanded(true)}
                aria-label="Filtry"
                aria-expanded={filtersExpanded}
              >
                <i className="bi bi-sliders" />
              </button>

              <button type="submit" className="efm-btn efm-btn--cta" aria-label="Szukaj">
                <i className="bi bi-arrow-right-circle" />
              </button>
            </div>
          </div>

          {/* Portal: backdrop + sheet do document.body */}
          {mobileSheet}
        </>
      )}
    </form>
  );
}
